import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth.js'
import LanguagePicker from './LanguagePicker.js'

interface SidebarProps {
  theme: 'light' | 'dark'
  onToggleTheme: () => void
}

const NAV_ITEMS = [
  { path: '/dashboard', tip: 'nav.watch',     icon: MonitorIcon },
  { path: '/extension', tip: 'nav.extension', icon: GlobeIcon },
  { path: '/usage',     tip: 'nav.usage',     icon: HexagonIcon },
] as const

export default function Sidebar({ theme, onToggleTheme }: SidebarProps) {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const location = useLocation()
  const nav = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    nav('/')
  }

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const initials = user?.displayName
    ? user.displayName.slice(0, 2).toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? '??')

  return (
    <aside className="app-sidebar">
      <div className="sidebar-logo" onClick={() => nav('/dashboard')}>
        <SilkVoiceLogo />
      </div>

      {NAV_ITEMS.map(item => (
        <button
          key={item.path}
          className={`sidebar-btn${location.pathname === item.path ? ' active' : ''}`}
          data-tip={t(item.tip)}
          onClick={() => nav(item.path)}
        >
          <item.icon />
        </button>
      ))}

      <div className="sidebar-spacer" />

      <LanguagePicker variant="compact" position="right" />

      <button className="sidebar-theme" onClick={onToggleTheme} aria-label={t('nav.toggleTheme')}>
        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <div className="sidebar-avatar" onClick={() => setMenuOpen(o => !o)} style={{ cursor: 'pointer' }}>
          {initials}
        </div>
        {menuOpen && (
          <div className="avatar-menu">
            <button className="avatar-menu-item" onClick={() => { setMenuOpen(false); nav('/usage') }}>
              <HexagonIcon />
              {t('nav.usage')}
            </button>
            <button className="avatar-menu-item" onClick={() => { setMenuOpen(false); nav('/settings') }}>
              <SettingsIcon />
              {t('nav.settings', 'Settings')}
            </button>
            <div className="avatar-menu-divider" />
            <button className="avatar-menu-item danger" onClick={handleSignOut}>
              <LogOutIcon />
              {t('nav.signOut')}
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

/* ── SVG Icons ─────────────────────────────────────────────────────────── */

function SilkVoiceLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sl1" x1="300" y1="250" x2="300" y2="750" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="50%" stopColor="#FF006E" />
          <stop offset="100%" stopColor="#00F5D4" />
        </linearGradient>
        <linearGradient id="sl2" x1="300" y1="250" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
        <linearGradient id="sl3" x1="300" y1="750" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#00F5D4" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
      </defs>
      <path d="M300 250 C150 400,150 600,300 750 C400 650,500 600,500 500 C500 400,400 350,300 250Z" fill="url(#sl1)" opacity=".9" />
      <path d="M300 750 C500 850,700 680,800 500 C680 480,550 500,440 500 C440 580,400 650,300 750Z" fill="url(#sl3)" />
      <path d="M300 250 C500 150,700 320,800 500 C680 520,550 500,440 500 C440 420,400 350,300 250Z" fill="url(#sl2)" />
    </svg>
  )
}

function MonitorIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )
}



function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
    </svg>
  )
}

function HexagonIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  )
}

function LogOutIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  )
}
